/* @jsx h */
import { defineComponent, h } from "vue";
import { resolveWalletDisplayTemplate } from "@lemmonhq/blogic/wallet-design";
import { newContentKey, previewBindings, removeContentField } from "../../document";
import type {
  WalletBarcodeSymbology,
  WalletContentField,
  WalletPassDesign,
  WalletPassDesignDocument,
} from "../../types";
import {
  EmptyHint,
  PanelRow,
  PanelSection,
  TemplateInput,
  ToggleRow,
} from "./primitives";

const SYMBOLOGIES: { value: WalletBarcodeSymbology; label: string }[] = [
  { value: "qr", label: "QR" },
  { value: "pdf417", label: "PDF417" },
  { value: "aztec", label: "Aztec" },
  { value: "code128", label: "Code 128" },
];

const FORMATS: { value: NonNullable<WalletContentField["format"]>; label: string }[] =
  [
    { value: "text", label: "Text" },
    { value: "number", label: "Number" },
    { value: "date", label: "Date" },
    { value: "currency", label: "Currency" },
  ];

interface FieldsPanelProps {
  design: WalletPassDesign;
  onChange: (patch: Partial<WalletPassDesignDocument>) => void;
  /** Replaces the whole document, for edits that touch placements too. */
  onReplace: (document: WalletPassDesignDocument) => void;
}

/**
 * Content: every field, the barcode and the links, defined once. The Apple,
 * Google and Browser tabs only choose where each field appears.
 */
export const FieldsPanel = defineComponent<FieldsPanelProps>({
  name: "FieldsPanel",
  props: ["design", "onChange", "onReplace"],
  setup(props) {
    const content = () => props.design.document.content;
    const patchContent = (patch: Partial<WalletPassDesignDocument["content"]>) =>
      props.onChange({ content: { ...content(), ...patch } });

    const updateField = (index: number, patch: Partial<WalletContentField>) => {
      const fields = [...content().fields];
      fields[index] = { ...fields[index], ...patch };
      patchContent({ fields });
    };

    const sample = (template: string) =>
      resolveWalletDisplayTemplate(
        template,
        props.design.kind,
        previewBindings(props.design.kind),
      );

    return () => (
      <div class="flex flex-col gap-4">
        <PanelSection
          title="Fields"
          description="Values are bindings resolved per customer. Place them on each platform in its own tab."
        >
          {content().fields.length === 0 ? (
            <EmptyHint icon="text_fields" text="No fields yet" />
          ) : (
            <div class="flex flex-col gap-3">
              {content().fields.map((field, index) => (
                <div
                  key={field.key}
                  class="p-3 rounded-lg border border-base-content/10 bg-base-200/30 flex flex-col gap-2"
                >
                  <div class="flex items-center gap-2">
                    <input
                      class="input input-bordered input-xs flex-1"
                      placeholder="LABEL"
                      value={field.label}
                      onInput={(e) =>
                        updateField(index, {
                          label: (e.target as HTMLInputElement).value,
                        })
                      }
                    />
                    <span class="badge badge-ghost badge-xs font-mono">
                      {field.key}
                    </span>
                    <button
                      class="btn btn-xs btn-ghost text-error"
                      title="Remove field and its placements"
                      onClick={() =>
                        props.onReplace(
                          removeContentField(props.design.document, field.key),
                        )
                      }
                    >
                      <span class="material-symbols-outlined text-sm">
                        delete
                      </span>
                    </button>
                  </div>

                  <TemplateInput
                    kind={props.design.kind}
                    value={field.value}
                    placeholder="{{customer.name}}"
                    onChange={(value) => updateField(index, { value })}
                  />

                  <div class="flex items-center gap-2">
                    <select
                      class="select select-bordered select-xs w-28"
                      value={field.format ?? "text"}
                      onChange={(e) =>
                        updateField(index, {
                          format: (e.target as HTMLSelectElement)
                            .value as WalletContentField["format"],
                        })
                      }
                    >
                      {FORMATS.map((format) => (
                        <option key={format.value} value={format.value}>
                          {format.label}
                        </option>
                      ))}
                    </select>
                    <input
                      class="input input-bordered input-xs flex-1 min-w-0"
                      placeholder="Update alert, e.g. You now have %@"
                      value={field.change_message ?? ""}
                      onInput={(e) => {
                        const value = (e.target as HTMLInputElement).value;
                        updateField(index, {
                          change_message: value || undefined,
                        });
                      }}
                    />
                  </div>

                  <div class="text-[11px] text-base-content/50 flex items-center gap-1.5">
                    <span class="material-symbols-outlined text-[13px]">
                      visibility
                    </span>
                    <span class="truncate">
                      {sample(field.value) ||
                        "empty for the sample — the field is dropped"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          <button
            class="btn btn-sm btn-outline w-full"
            onClick={() =>
              patchContent({
                fields: [
                  ...content().fields,
                  {
                    key: newContentKey(props.design.document, "field"),
                    label: "NEW FIELD",
                    value: "",
                  },
                ],
              })
            }
          >
            <span class="material-symbols-outlined text-sm">add</span>
            Add field
          </button>
        </PanelSection>

        <PanelSection
          title="Barcode"
          description="What the till scanner reads, on every platform."
        >
          <ToggleRow
            label="Show a barcode"
            value={!!content().barcode}
            onChange={(on) =>
              patchContent({
                barcode: on
                  ? {
                      symbology: "qr",
                      message:
                        props.design.kind === "membership" ||
                        props.design.kind === "coin"
                          ? "{{loyalty.qr}}"
                          : "",
                      alt_text: "",
                    }
                  : null,
              })
            }
          />
          {content().barcode && (
            <>
              <PanelRow label="Format">
                <select
                  class="select select-bordered select-sm"
                  value={content().barcode!.symbology}
                  onChange={(e) =>
                    patchContent({
                      barcode: {
                        ...content().barcode!,
                        symbology: (e.target as HTMLSelectElement)
                          .value as WalletBarcodeSymbology,
                      },
                    })
                  }
                >
                  {SYMBOLOGIES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </PanelRow>
              <PanelRow label="Encoded value">
                <TemplateInput
                  kind={props.design.kind}
                  value={content().barcode!.message}
                  onChange={(message) =>
                    patchContent({
                      barcode: { ...content().barcode!, message },
                    })
                  }
                />
              </PanelRow>
              <PanelRow
                label="Text under the code"
                hint="Optional, e.g. the short member ID."
              >
                <TemplateInput
                  kind={props.design.kind}
                  value={content().barcode!.alt_text}
                  onChange={(alt_text) =>
                    patchContent({
                      barcode: { ...content().barcode!, alt_text },
                    })
                  }
                />
              </PanelRow>
            </>
          )}
        </PanelSection>

        <PanelSection
          title="Links"
          description="Web, phone (tel:) or email (mailto:) links. Apple shows them on the back, Google in the links module, the browser card on its panel."
        >
          {content().links.length === 0 ? (
            <EmptyHint icon="link" text="No links" />
          ) : (
            <div class="flex flex-col gap-2">
              {content().links.map((link, index) => (
                <div key={link.key} class="flex items-center gap-2">
                  <input
                    class="input input-bordered input-xs w-28"
                    value={link.label}
                    onInput={(e) => {
                      const links = [...content().links];
                      links[index] = {
                        ...link,
                        label: (e.target as HTMLInputElement).value,
                      };
                      patchContent({ links });
                    }}
                  />
                  <input
                    class="input input-bordered input-xs flex-1 min-w-0 font-mono"
                    placeholder="https://…"
                    value={link.url}
                    onInput={(e) => {
                      const links = [...content().links];
                      links[index] = {
                        ...link,
                        url: (e.target as HTMLInputElement).value,
                      };
                      patchContent({ links });
                    }}
                  />
                  <button
                    class="btn btn-xs btn-ghost text-error"
                    onClick={() =>
                      patchContent({
                        links: content().links.filter((_, i) => i !== index),
                      })
                    }
                  >
                    <span class="material-symbols-outlined text-sm">
                      delete
                    </span>
                  </button>
                </div>
              ))}
            </div>
          )}
          <button
            class="btn btn-sm btn-outline w-full"
            disabled={content().links.length >= 10}
            onClick={() =>
              patchContent({
                links: [
                  ...content().links,
                  {
                    key: newContentKey(props.design.document, "link"),
                    label: "Website",
                    url: "https://",
                  },
                ],
              })
            }
          >
            <span class="material-symbols-outlined text-sm">add</span>
            Add link
          </button>
        </PanelSection>
      </div>
    );
  },
});
